import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";

/**
 * QueuePoster — branded printable queue QR poster modal
 *
 * Props:
 *   queueId      string   required
 *   businessName string   required
 *   queueLabel   string
 *   appDomain    string   e.g. "qkard.app" or "lucknowmarkets.com"
 *   accentColor  string   brand color e.g. "#6c63ff" or "#FF6B35"
 *   brandName    string   e.g. "QKARD" or "Lucknow Markets"
 *   onClose      func     required
 */
export default function QueuePoster({
  queueId,
  businessName,
  queueLabel,
  appDomain = "qkard.app",
  accentColor = "#6c63ff",
  brandName = "QKARD",
  onClose,
}) {
  const canvasRef = useRef(null);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const queueUrl = `https://${appDomain}/q/${queueId}`;

  useEffect(() => {
    QRCode.toDataURL(queueUrl, {
      width: 400, margin: 2,
      color: { dark: accentColor, light: "#ffffff" },
      errorCorrectionLevel: "H",
    }).then(setQrDataUrl);
  }, [queueUrl, accentColor]);

  // ── Download poster as PNG ────────────────────────────────────────────────
  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas || !qrDataUrl) return;
    const ctx = canvas.getContext("2d");
    const W = 800, H = 1100;
    canvas.width = W;
    canvas.height = H;

    // Background
    ctx.fillStyle = "#0f0e17";
    ctx.fillRect(0, 0, W, H);

    // Top accent bar
    const grad = ctx.createLinearGradient(0, 0, W, 0);
    grad.addColorStop(0, accentColor);
    grad.addColorStop(1, "#4facfe");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, 8);

    // Shop name
    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "center";
    ctx.font = "bold 52px sans-serif";
    ctx.fillText(businessName || "Your Shop", W / 2, 100);

    // Queue label
    ctx.fillStyle = "#9ca3af";
    ctx.font = "28px sans-serif";
    ctx.fillText(queueLabel || "Main Queue", W / 2, 148);

    // Divider
    ctx.strokeStyle = "#1e1e2e";
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(80, 176); ctx.lineTo(W - 80, 176); ctx.stroke();

    // JOIN QUEUE
    ctx.fillStyle = accentColor;
    ctx.font = "bold 20px sans-serif";
    ctx.fillText("JOIN QUEUE", W / 2, 222);

    // White QR card
    const qrX = W / 2 - 220, qrY = 248, qrS = 440;
    roundRect(ctx, qrX, qrY, qrS, qrS, 24);
    ctx.fillStyle = "#ffffff"; ctx.fill();

    // QR image
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, qrX + 20, qrY + 20, qrS - 40, qrS - 40);

      // Instruction pill
      roundRect(ctx, W / 2 - 210, 718, 420, 48, 24);
      ctx.fillStyle = "#1a1a2e"; ctx.fill();
      ctx.fillStyle = "#a5b4fc";
      ctx.font = "500 17px sans-serif";
      ctx.fillText("Scan with camera → tap the link → join", W / 2, 748);

      // Green badges
      ctx.fillStyle = "#22c55e";
      ctx.font = "bold 15px sans-serif";
      ctx.fillText("✓ No payment   ✓ No app needed   ✓ Free", W / 2, 804);

      // URL
      ctx.fillStyle = "#4b5563";
      ctx.font = "13px monospace";
      ctx.fillText(queueUrl, W / 2, 840);

      // Divider
      ctx.strokeStyle = "#1e1e2e";
      ctx.beginPath(); ctx.moveTo(80, 864); ctx.lineTo(W - 80, 864); ctx.stroke();

      // Brand
      ctx.fillStyle = accentColor;
      ctx.font = "bold 22px sans-serif";
      ctx.fillText(brandName, W / 2, 906);
      ctx.fillStyle = "#4b5563";
      ctx.font = "14px sans-serif";
      ctx.fillText("Digital Queue Management", W / 2, 932);

      // Bottom accent bar
      const grad2 = ctx.createLinearGradient(0, H - 8, W, H);
      grad2.addColorStop(0, accentColor);
      grad2.addColorStop(1, "#4facfe");
      ctx.fillStyle = grad2;
      ctx.fillRect(0, H - 8, W, 8);

      const a = document.createElement("a");
      a.download = `queue-poster-${(businessName || "shop").replace(/\s+/g, "-").toLowerCase()}.png`;
      a.href = canvas.toDataURL("image/png");
      a.click();
    };
    img.src = qrDataUrl;
  };

  function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y); ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r); ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h); ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r); ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }

  return (
    <div style={s.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={s.modal}>

        {/* Header */}
        <div style={s.header}>
          <div>
            <div style={s.headerTitle}>Queue Poster</div>
            <div style={s.headerSub}>Print and display at your shop</div>
          </div>
          <button style={s.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* Poster preview */}
        <div style={s.poster}>
          <div style={{ ...s.accentBar, background: `linear-gradient(90deg, ${accentColor}, #4facfe)` }} />

          <div style={s.shopInfo}>
            <div style={s.shopName}>{businessName || "Your Shop"}</div>
            <div style={s.shopLabel}>{queueLabel || "Main Queue"}</div>
          </div>

          <div style={s.divider} />

          <div style={{ ...s.joinHeading, color: accentColor }}>JOIN QUEUE</div>

          <div style={s.qrCard}>
            {qrDataUrl
              ? <img src={qrDataUrl} alt="Queue QR" style={s.qrImg} />
              : <div style={s.qrLoading}>Generating…</div>
            }
          </div>

          <div style={s.pill}>
            📱 Scan with camera → tap link → join
          </div>

          <div style={s.badges}>
            <span style={s.badge}>✓ No payment</span>
            <span style={s.badge}>✓ No app needed</span>
            <span style={s.badge}>✓ Free</span>
          </div>

          <div style={s.urlText}>{queueUrl}</div>

          <div style={s.divider} />

          <div style={s.branding}>
            <span style={{ ...s.brandName2, color: accentColor }}>{brandName}</span>
            <span style={s.brandSub}>Digital Queue Management</span>
          </div>

          <div style={{ ...s.accentBar, background: `linear-gradient(90deg, ${accentColor}, #4facfe)` }} />
        </div>

        {/* Actions */}
        <div style={s.actions}>
          <button
            style={{ ...s.downloadBtn, background: accentColor }}
            onClick={handleDownload}
            disabled={!qrDataUrl}
          >
            ⬇ Download PNG (Print Ready)
          </button>
          <button style={s.shareBtn}
            onClick={() => window.open(`https://wa.me/?text=Join+our+queue+here:+${encodeURIComponent(queueUrl)}`, "_blank")}>
            📲 Share on WhatsApp
          </button>
        </div>

        <canvas ref={canvasRef} style={{ display: "none" }} />
      </div>
    </div>
  );
}

const s = {
  overlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)",
    zIndex: 300, display: "flex", alignItems: "center",
    justifyContent: "center", padding: 16,
  },
  modal: {
    background: "#fff", borderRadius: 20,
    width: "100%", maxWidth: 420,
    maxHeight: "95vh", overflowY: "auto",
    padding: "20px 20px 24px",
  },
  header: {
    display: "flex", alignItems: "flex-start",
    justifyContent: "space-between", marginBottom: 16,
  },
  headerTitle: { fontSize: 18, fontWeight: 700, color: "#111" },
  headerSub: { fontSize: 13, color: "#888", marginTop: 2 },
  closeBtn: {
    background: "#f5f5f3", border: "none", borderRadius: 8,
    width: 32, height: 32, fontSize: 14, cursor: "pointer",
    color: "#666", flexShrink: 0,
  },
  poster: { background: "#0f0e17", borderRadius: 16, overflow: "hidden", marginBottom: 16 },
  accentBar: { height: 6 },
  shopInfo: { padding: "20px 20px 0", textAlign: "center" },
  shopName: { fontSize: 26, fontWeight: 800, color: "#fff", letterSpacing: "-0.5px", marginBottom: 4 },
  shopLabel: { fontSize: 14, color: "#9ca3af" },
  divider: { height: 1, background: "#1e1e2e", margin: "16px 24px" },
  joinHeading: { textAlign: "center", fontSize: 11, fontWeight: 800, letterSpacing: 4, marginBottom: 14 },
  qrCard: {
    background: "#fff", borderRadius: 16, margin: "0 20px",
    padding: 12, display: "flex", alignItems: "center",
    justifyContent: "center", minHeight: 180,
  },
  qrImg: { width: "100%", height: "auto", display: "block", borderRadius: 8 },
  qrLoading: { color: "#aaa", fontSize: 14 },
  pill: {
    margin: "14px 20px 0", background: "#1a1a2e",
    borderRadius: 24, padding: "10px 16px",
    textAlign: "center", fontSize: 12, fontWeight: 600, color: "#a5b4fc",
  },
  badges: {
    display: "flex", justifyContent: "center",
    gap: 10, margin: "10px 16px 0", flexWrap: "wrap",
  },
  badge: { fontSize: 11, fontWeight: 700, color: "#22c55e" },
  urlText: {
    textAlign: "center", fontSize: 10, color: "#4b5563",
    fontFamily: "monospace", margin: "8px 16px 0", wordBreak: "break-all",
  },
  branding: {
    textAlign: "center", padding: "12px 0 14px",
    display: "flex", flexDirection: "column", gap: 2,
  },
  brandName2: { fontSize: 16, fontWeight: 800, letterSpacing: 2 },
  brandSub: { fontSize: 11, color: "#4b5563" },
  actions: { display: "flex", flexDirection: "column", gap: 10 },
  downloadBtn: {
    width: "100%", color: "#fff", border: "none",
    borderRadius: 12, padding: 14, fontSize: 15, fontWeight: 700, cursor: "pointer",
  },
  shareBtn: {
    width: "100%", background: "#25D366", color: "#fff", border: "none",
    borderRadius: 12, padding: 14, fontSize: 15, fontWeight: 700, cursor: "pointer",
  },
};
